import { z } from 'zod';
import { propertyLandAccessInputSchema } from './property-land-access.schema';
import { PROPERTY_TITLE_MAX_LENGTH } from '../constants/property-title.constant';
import {
  PropertyMediaKind,
  PropertyExternalMediaMatterportVirtualTourInput as MatterportExternalMedia,
  PropertyExternalMediaYoutubeVideoInput as YoutubeExternalMedia,
  PropertyOperation,
  PropertyOrientation,
  PropertySubtype,
} from '../../../api/generated';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

const optionalNonNegativeNumberString = (params?: {
  integer?: boolean;
  labelKey?: TranslationKey;
  t?: I18nContextValue['t'];
}) => {
  const t = params?.t;
  const label = params?.labelKey
    ? t?.(params.labelKey) ?? params.labelKey
    : 'El valor';
  const requireInteger = params?.integer ?? false;

  return z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value?.trim()) {
          return true;
        }

        const normalized = value.trim().replace(',', '.');
        const parsed = Number(normalized);

        if (!Number.isFinite(parsed) || parsed < 0) {
          return false;
        }

        return requireInteger ? Number.isInteger(parsed) : true;
      },
      {
        message: requireInteger
          ? t?.('catalog.properties.validation.nonNegativeInteger', {
              field: label,
            }) ?? `${label} debe ser un número entero positivo`
          : t?.('catalog.properties.validation.nonNegativeNumber', {
              field: label,
            }) ?? `${label} debe ser un número positivo`,
      }
    );
};

const propertyMediaInputSchema = z.object({
  fileId: z.string().min(1),
  kind: z.enum(PropertyMediaKind).default(PropertyMediaKind.IMAGE),
  position: z.number().int().min(0).optional(),
  sourceUrl: z.string().optional().nullable(),
  sourceSystem: z.string().optional().nullable(),
  sourceId: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  altText: z.string().optional().nullable(),
});

const externalMediaUrlSchema = (
  allowedHosts: readonly string[],
  t?: I18nContextValue['t']
) =>
  z.url(t?.('catalog.properties.validation.invalidUrl') ?? 'URL inválida').pipe(
    z
      .string()
      .refine(
        (url) => allowedHosts.includes(new URL(url).hostname.toLowerCase()),
        {
          message:
            t?.('catalog.properties.validation.invalidUrl') ?? 'URL inválida',
        }
      )
  );

export const buildPropertyFormSchema = (t?: I18nContextValue['t']) =>
  z.object({
    userId: z.number().int().positive().nullable().optional(),
    name: z
      .string()
      .min(
        1,
        t?.('catalog.properties.validation.nameRequired') ??
          'El nombre es obligatorio'
      )
      .refine((name) => Array.from(name).length <= PROPERTY_TITLE_MAX_LENGTH, {
        message:
          t?.('catalog.properties.validation.nameTooLong', {
            max: PROPERTY_TITLE_MAX_LENGTH,
          }) ??
          `El nombre no puede superar los ${PROPERTY_TITLE_MAX_LENGTH} caracteres`,
      }),
    description: z.string().optional(),
    isActive: z.boolean().default(true),
    operation: z
      .enum(PropertyOperation)
      .default(PropertyOperation.SALE_AND_RENT),
    salePriceAmount: optionalNonNegativeNumberString({
      labelKey: 'catalog.properties.form.salePriceAmount',
      t,
    }),
    salePriceCurrency: z.string().optional(),
    rentPriceAmount: optionalNonNegativeNumberString({
      labelKey: 'catalog.properties.form.rentPriceAmount',
      t,
    }),
    rentPriceCurrency: z.string().optional(),
    sourceUrl: z
      .url(t?.('catalog.properties.validation.invalidUrl') ?? 'URL inválida')
      .optional()
      .or(z.literal('')),
    sourceSystem: z.string().optional(),
    sourceId: z.string().optional(),
    sourceCode: z.string().optional(),
    mercadoLibreCode: z.string().optional(),
    cadastralId: z.string().optional(),
    landAccess: propertyLandAccessInputSchema.nullable().optional(),
    countryCode: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{2}$/)
      .optional()
      .or(z.literal('')),
    locality: z.string().optional(),
    region: z.string().optional(),
    addressDisplay: z.string().optional(),
    streetName: z.string().optional(),
    streetNumber: z.string().optional(),
    unit: z.string().optional(),
    neighborhood: z.string().optional(),
    externalMedia: z
      .array(
        z.discriminatedUnion('provider', [
          z.object({
            kind: z.literal(YoutubeExternalMedia.kind.VIDEO),
            provider: z.literal(YoutubeExternalMedia.provider.YOUTUBE),
            url: externalMediaUrlSchema(
              ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be'],
              t
            ),
          }),
          z.object({
            kind: z.literal(MatterportExternalMedia.kind.VIRTUAL_TOUR),
            provider: z.literal(MatterportExternalMedia.provider.MATTERPORT),
            url: externalMediaUrlSchema(['my.matterport.com'], t),
          }),
        ])
      )
      .default([]),
    media: z.array(propertyMediaInputSchema).default([]),
    latitude: z.string().optional(),
    longitude: z.string().optional(),
    propertyType: z
      .enum([
        'HOUSE',
        'APARTMENT',
        'OFFICE',
        'RETAIL',
        'WAREHOUSE',
        'LAND',
        'PARCEL',
        'PARKING',
        'INDUSTRIAL',
        'OTHER',
      ])
      .optional()
      .nullable(),
    propertySubtype: z.enum(PropertySubtype).nullable().optional(),
    condition: z.enum(['USED', 'NEW']).default('USED'),
    bedrooms: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.bedrooms',
      t,
    }),
    bathrooms: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.bathrooms',
      t,
    }),
    rooms: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.rooms',
      t,
    }),
    parkingLots: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.parkingLots',
      t,
    }),
    warehouses: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.warehouses',
      t,
    }),
    unitFloor: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.unitFloor',
      t,
    }),
    floors: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.floors',
      t,
    }),
    apartmentsPerFloor: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.apartmentsPerFloor',
      t,
    }),
    propertyAge: optionalNonNegativeNumberString({
      integer: true,
      labelKey: 'catalog.properties.form.propertyAge',
      t,
    }),
    orientation: z.enum(PropertyOrientation).optional().nullable(),
    areaUsable: optionalNonNegativeNumberString({
      labelKey: 'catalog.properties.form.surfaceUsable',
      t,
    }),
    areaBuilt: optionalNonNegativeNumberString({
      labelKey: 'catalog.properties.form.surfaceCovered',
      t,
    }),
    areaTotal: optionalNonNegativeNumberString({
      labelKey: 'catalog.properties.form.surfaceTotal',
      t,
    }),
    areaOutdoor: optionalNonNegativeNumberString({
      labelKey: 'catalog.properties.form.surfaceOutdoor',
      t,
    }),
    commonExpensesAmount: optionalNonNegativeNumberString({
      labelKey: 'catalog.properties.form.commonExpensesAmount',
      t,
    }),
    furnished: z.boolean().nullable().optional(),
    petsAllowed: z.boolean().nullable().optional(),
    hasVirtualTour: z.boolean().nullable().optional(),
    hasBalcony: z.boolean().nullable().optional(),
    hasKitchen: z.boolean().nullable().optional(),
    hasLift: z.boolean().nullable().optional(),
    hasPlayground: z.boolean().nullable().optional(),
    hasRoofGarden: z.boolean().nullable().optional(),
    hasBusinessCenter: z.boolean().nullable().optional(),
    hasCinemaHall: z.boolean().nullable().optional(),
    hasTennisCourt: z.boolean().nullable().optional(),
    hasGarden: z.boolean().nullable().optional(),
    hasPatio: z.boolean().nullable().optional(),
    hasTerrace: z.boolean().nullable().optional(),
    hasLaundry: z.boolean().nullable().optional(),
    hasPool: z.boolean().nullable().optional(),
    hasGym: z.boolean().nullable().optional(),
    hasSecurity: z.boolean().nullable().optional(),
    hasHeating: z.boolean().nullable().optional(),
    hasAirConditioning: z.boolean().nullable().optional(),
    hasInternetAccess: z.boolean().nullable().optional(),
  });

export const propertyFormSchema = buildPropertyFormSchema();

export type PropertyFormValues = z.infer<
  ReturnType<typeof buildPropertyFormSchema>
>;

export type PropertyFormInputValues = z.input<
  ReturnType<typeof buildPropertyFormSchema>
>;
